import { useState, useCallback } from 'react';
import { useAppDispatch } from '../redux/hooks';
import { addToCart, openCart } from '../redux/slices/cartSlice';
import { createCartItem } from '../utils/cartUtils';
import { shouldShowSpiceLevel } from '../utils/spiceUtils';
import { filterEnabledModifiers, isModifierRequired } from '../utils/modifierUtils';

interface UseMenuItemCartOptions {
  autoOpenCart?: boolean;
  onBuilderItemClick?: (item: any) => void;
}

/**
 * Reusable hook for handling menu item cart operations
 * Consolidates duplicate logic from HomePage, MenuPage, ViewCartPage, and Cart
 */
export const useMenuItemCart = (options: UseMenuItemCartOptions = {}) => {
  const { autoOpenCart = false, onBuilderItemClick } = options;
  const dispatch = useAppDispatch();
  
  const [modalItem, setModalItem] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Check if item needs customization (spice level or modifiers)
   */
  const needsCustomization = useCallback((item: any): boolean => {
    const hasSpiceLevel = shouldShowSpiceLevel(item);
    const enabledModifiers = filterEnabledModifiers(item.modifiers_list || []);
    const hasRequiredModifiers = enabledModifiers.some(
      modifier => isModifierRequired(modifier)
    );
    return hasSpiceLevel || hasRequiredModifiers;
  }, []);

  /**
   * Main handler to add menu items to cart
   * Automatically checks for builder items (salad bar)
   */
  const handleAddToCart = useCallback((item: any) => {
    // Always check for builder items (salad bar)
    const isSaladBarItem = item.builder_type?.toLowerCase() === 'salad';
    
    if (isSaladBarItem && onBuilderItemClick) {
      onBuilderItemClick(item);
      return;
    }

    // Regular menu item logic
    if (needsCustomization(item)) {
      setModalItem(item);
      setIsModalOpen(true);
    } else {
      const cartItem = createCartItem(item, null, [], 1);
      dispatch(addToCart(cartItem));
      
      // Feature flag: only open cart if enabled
      if (autoOpenCart) {
        dispatch(openCart());
      }
    }
  }, [dispatch, needsCustomization, autoOpenCart, onBuilderItemClick]);

  /**
   * Close the customization modal
   */
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setModalItem(null);
  }, []);

  /**
   * Add item to cart with customizations from modal
   */
  const handleModalAddToCart = useCallback((
    item: any,
    quantity: number,
    spiceLevel: number | null,
    modifiers: any[]
  ) => {
    const cartItem = createCartItem(item, spiceLevel, modifiers, quantity);
    dispatch(addToCart(cartItem));
    setIsModalOpen(false);
    setModalItem(null);
    
    // Feature flag: only open cart if enabled
    if (autoOpenCart) {
      dispatch(openCart());
    }
  }, [dispatch, autoOpenCart]);

  /**
   * Open modal with specific item (for external control)
   */
  const openModal = useCallback((item: any) => {
    setModalItem(item);
    setIsModalOpen(true);
  }, []);

  return {
    // State
    modalItem,
    isModalOpen,
    
    // Handlers
    handleAddToCart,
    handleModalClose,
    handleModalAddToCart,
    needsCustomization,
    openModal,
    
    // Modal props for easy spreading
    modalProps: {
      isOpen: isModalOpen,
      item: modalItem,
      onClose: handleModalClose,
      onAddToCart: handleModalAddToCart,
    },
  };
};
